import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";

import {
  IAssignSupplierCompanyDtoQueryReq,
  IAssignSupplierCompanyToShowQueryReq,
  IChangeInvoiceEmailReq,
  IChangeStatusSupplierCompanyDtoReq,
  IChangeSupplierCompanyRecommendationQueryReq,
  ICreateSupplierCompanyWithSupplierReq,
  IDeleteSupplierCompanyToShowQueryReq,
  IEditProductCategoryCodeQueryReq,
  IEditSupplierCompanyToShowQueryReq,
  IGetAllByIdsSupplierCompanyReq,
  IGetAllByShowIdSupplierCompanyReq,
  IGetAllSupplierCompaniesAssignedToShowQueryReq,
  IGetAllSupplierCompaniesShowProductCategoryQueryReq,
  IGetAllSupplierCompaniesToProductCategoryQueryReq,
  IGetManySupplierCompanyByAdminQueryReq,
  IGetManySupplierCompanyQueryReq,
  IGetOneSupplierCompaniesToProductCategoryQueryReq,
  IGetOneSupplierCompanyQueryReq,
  IGetSupplierCompaniesAssignableToShowQueryReq,
  IGetSupplierCompanyByOrganiserCompanyQueryReq,
  IGetSupplierCompanyByOwnerDtoQueryReq,
  IGetSupplierCompanyShowManagerQueryReq,
  IUpdateSupplierCompanyReq,
} from "@evennt-tech/api-v2-external";

import {
  IManagerSupplierToShow,
  ISupplierCompany,
  ISupplierCompanyShowProductCategory,
  ISupplierCompanyToProductCategory,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { getErrorMessage } from "tools/error";
import { SupplierStoreTarget } from "ts/enums/store.enum";
import { registerStore } from "store/utils/reset-store.utils";

type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
};

type State = {
  itemsForAdmin: ISupplierCompany[];
  paginationForAdminItems: PaginationMeta;

  itemsForRecommended: ISupplierCompany[];
  paginationForRecommendedItems: PaginationMeta;

  itemsForOrganiserCompany: ISupplierCompany[];
  paginationForOrganiserCompany: PaginationMeta;

  itemsByOwner: ISupplierCompany[];

  itemsByShow: ISupplierCompany[];

  itemsByIds: ISupplierCompany[];

  supplierCompaniesListByOrganiserCompany: ISupplierCompany[];

  itemsAssignableToShow: ISupplierCompany[];

  itemsAssignedToShow: ISupplierCompany[];

  itemsSupplierCompanyProductCategory: ISupplierCompanyToProductCategory[];
  itemsSupplierCompanyShowProductCategory: ISupplierCompanyShowProductCategory[];

  selected: ISupplierCompany | null;

  selectedSupplierCompanyProductCategory: ISupplierCompanyToProductCategory | null;

  showManager: IManagerSupplierToShow | null;

  lastUsedAdminParams: IGetManySupplierCompanyByAdminQueryReq | null;
  lastUsedSupplierParams: IGetManySupplierCompanyQueryReq | null;

  isLoading: boolean;
  error: string | null;
};

type Action = {
  createSupplierCompanyWithSupplier: (
    body: ICreateSupplierCompanyWithSupplierReq
  ) => Promise<void>;

  fetchMany: (
    params: IGetManySupplierCompanyQueryReq,
    target: SupplierStoreTarget
  ) => Promise<void>;

  fetchManyByAdmin: (
    params: IGetManySupplierCompanyByAdminQueryReq
  ) => Promise<void>;

  fetchOne: (params: IGetOneSupplierCompanyQueryReq) => Promise<void>;

  fetchOneSupplierCompaniesToProductCategory: (
    params: IGetOneSupplierCompaniesToProductCategoryQueryReq
  ) => Promise<void>;

  fetchAllSupplierCompaniesToProductCategory: (
    params: IGetAllSupplierCompaniesToProductCategoryQueryReq
  ) => Promise<void>;

  fetchAllSupplierCompaniesShowProductCategory: (
    params: IGetAllSupplierCompaniesShowProductCategoryQueryReq
  ) => Promise<void>;

  fetchShowManager: (
    params: IGetSupplierCompanyShowManagerQueryReq
  ) => Promise<void>;

  fetchSupplierCompaniesAssignableToShow: (
    params: IGetSupplierCompaniesAssignableToShowQueryReq
  ) => Promise<void>;

  fetchSupplierCompaniesAssignedToShow: (
    params: IGetAllSupplierCompaniesAssignedToShowQueryReq
  ) => Promise<void>;

  edit: (body: IUpdateSupplierCompanyReq) => Promise<void>;

  changeRecommendations: (
    body: IChangeSupplierCompanyRecommendationQueryReq
  ) => Promise<void>;

  changeInvoiceEmails: (body: IChangeInvoiceEmailReq) => Promise<void>;

  changeStatus: (body: IChangeStatusSupplierCompanyDtoReq) => Promise<void>;

  assign: (body: IAssignSupplierCompanyDtoQueryReq) => Promise<void>;

  assignToShow: (body: IAssignSupplierCompanyToShowQueryReq) => Promise<void>;

  editToShow: (body: IEditSupplierCompanyToShowQueryReq) => Promise<void>;

  editProductCategoryCode: (
    body: IEditProductCategoryCodeQueryReq
  ) => Promise<void>;

  deleteFromShow: (body: IDeleteSupplierCompanyToShowQueryReq) => Promise<void>;

  fetchByOrganiserCompany: (
    params: IGetSupplierCompanyByOrganiserCompanyQueryReq
  ) => Promise<void>;

  fetchByOwner: (
    params: IGetSupplierCompanyByOwnerDtoQueryReq
  ) => Promise<void>;

  setItemsByOwner: (items: ISupplierCompany[]) => void;

  fetchByShow: (params: IGetAllByShowIdSupplierCompanyReq) => Promise<void>;

  fetchAllByIds: (params: IGetAllByIdsSupplierCompanyReq) => Promise<void>;

  reload: (target: SupplierStoreTarget) => Promise<void>;

  autoCatch: (target: SupplierStoreTarget) => void;

  autoCatchForAssignedToShow: (
    params: IGetAllSupplierCompaniesAssignedToShowQueryReq
  ) => void;

  autoCatchForAssignableToShow: (
    params: IGetSupplierCompaniesAssignableToShowQueryReq
  ) => void;

  setSelected: (supplierCompany: ISupplierCompany | null) => void;
  getSelected: () => ISupplierCompany | null;
  setError: (message: string | null) => void;
  clearSelected: () => void;
};

export const useSupplierCompanies = create<State & Action>()(
  devtools((set, get) => ({
    itemsForAdmin: [],
    paginationForAdminItems: { page: 1, limit: 0, total: 0 },

    itemsForRecommended: [],
    paginationForRecommendedItems: { page: 1, limit: 0, total: 0 },

    itemsForOrganiserCompany: [],
    paginationForOrganiserCompany: { page: 1, limit: 0, total: 0 },

    itemsByOwner: [],

    itemsByShow: [],

    itemsByIds: [],

    supplierCompaniesListByOrganiserCompany: [],

    itemsAssignableToShow: [],

    itemsAssignedToShow: [],

    itemsSupplierCompanyProductCategory: [],
    itemsSupplierCompanyShowProductCategory: [],

    lastUsedAdminParams: null,
    lastUsedSupplierParams: null,

    selected: null,
    selectedSupplierCompanyProductCategory: null,
    showManager: null,
    isLoading: false,
    error: null,

    setError: (message) => set({ error: message }),

    setSelected: (supplierCompany) => set({ selected: supplierCompany }),
    getSelected: () => get().selected,

    setItemsByOwner: (items) =>
      set({
        itemsByOwner: items,
      }),

    fetchSupplierCompaniesAssignableToShow: async (params) => {
      set({ isLoading: true });
      try {
        const itemsByOrganiserCompany = await apiV2.supplierCompany.getSupplierCompaniesAssignableToShow(
          params
        );
        set({ itemsAssignableToShow: itemsByOrganiserCompany, error: null });
      } catch (e) {
        console.error(
          "Failed to fetch supplier companies assignable to show:",
          e
        );
        set({ itemsAssignableToShow: [] });
      } finally {
        set({ isLoading: false });
      }
    },

    fetchSupplierCompaniesAssignedToShow: async (params) => {
      set({ isLoading: true });
      try {
        const itemsByOrganiserCompany = await apiV2.supplierCompany.getAllSupplierCompaniesAssignedToShow(
          params
        );
        set({ itemsAssignedToShow: itemsByOrganiserCompany, error: null });
      } catch (e) {
        console.error(
          "Failed to fetch supplier companies assigned to show:",
          e
        );
        set({ itemsAssignedToShow: [] });
      } finally {
        set({ isLoading: false });
      }
    },

    fetchByShow: async (params) => {
      set({ isLoading: true });
      try {
        const itemsByShow = await apiV2.supplierCompany.getAllByShowId(params);
        set({ itemsByShow, error: null });
      } catch (e) {
        console.error("Failed to fetch supplier companies by show:", e);
        set({ itemsByShow: [] });
      } finally {
        set({ isLoading: false });
      }
    },

    fetchAllByIds: async (params) => {
      try {
        const itemsByIds = await apiV2.supplierCompany.getAllByIds(params);
        set({ itemsByIds, error: null });
      } catch (e) {
        console.error("Failed to fetch supplier companies by ids:", e);
        set({ itemsByIds: [] });
      }
    },

    assignToShow: async (body) => {
      try {
        const res = await apiV2.supplierCompany.assignToShow(body);
        set((state) => {
          return {
            itemsAssignedToShow: [...state.itemsAssignedToShow, res],
            itemsAssignableToShow: state.itemsAssignableToShow.filter(
              (item) => item.id !== res.id
            ),
            error: null,
          };
        });
      } catch (e) {
        console.error("Failed to assign supplier company to show:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    editToShow: async (body) => {
      try {
        const res = await apiV2.supplierCompany.editToShow(body);
        set((state) => ({
          itemsAssignedToShow: state.itemsAssignedToShow.map((item) =>
            item.id === res.id ? res : item
          ),
          error: null,
        }));
      } catch (e) {
        console.error("Failed to edit supplier company to show:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    deleteFromShow: async (body) => {
      try {
        const res = await apiV2.supplierCompany.deleteFromShow(body);
        set((state) => {
          const updatedItems = state.itemsAssignedToShow.filter(
            (item) => item.id !== res.id
          );

          return {
            itemsAssignedToShow: updatedItems,
            error: null,
          };
        });
      } catch (e) {
        console.error("Failed to delete supplier company from show:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    createSupplierCompanyWithSupplier: async (body) => {
      try {
        const res = await apiV2.supplierCompany.createSupplierWithSupplierCompany(
          body
        );
        set((state) => ({
          itemsForAdmin: [...state.itemsForAdmin, res],
          itemsForOrganiserCompany: [...state.itemsForOrganiserCompany, res],
          error: null,
        }));
      } catch (e) {
        console.error("Failed to create supplier:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    fetchMany: async (params, target) => {
      set({ isLoading: true, lastUsedSupplierParams: params });
      try {
        const res = await apiV2.supplierCompany.getMany(params);

        const paginationData = {
          page: res.meta.currentPage,
          limit: res.meta.itemsPerPage,
          total: res.meta.totalItems ?? 0,
        };

        if (target === SupplierStoreTarget.ORGANISER_COMPANY) {
          set({
            itemsForOrganiserCompany: res.items ?? [],
            paginationForOrganiserCompany: paginationData,
            error: null,
          });
        } else if (target === SupplierStoreTarget.RECOMMENDED) {
          set({
            itemsForRecommended: res.items ?? [],
            paginationForRecommendedItems: paginationData,
            error: null,
          });
        }
      } catch (e) {
        console.error("Failed to fetch supplier companies:", e);
      } finally {
        set({ isLoading: false });
      }
    },

    fetchManyByAdmin: async (params) => {
      set({ isLoading: true, lastUsedAdminParams: params });
      try {
        const res = await apiV2.supplierCompany.getManyByAdmin(params);

        const paginationData = {
          page: res.meta.currentPage,
          limit: res.meta.itemsPerPage,
          total: res.meta.totalItems ?? 0,
        };

        const itemsWithNested = (res.items ?? []).map((supplierCompany) => {
          const supplierCompanyWithMap = supplierCompany as
            | (typeof supplierCompany & {
                supplierShowsCountMap: Record<string, number>;
              })
            | undefined;

          const nestedSuppliers =
            supplierCompany.supplierCompanyToSuppliers
              ?.map((relation) => {
                const supplier = relation?.supplier;
                if (!supplier) return null;
                return {
                  ...supplier,
                  showsCount:
                    supplierCompanyWithMap?.supplierShowsCountMap?.[
                      supplier.id
                    ] ?? 0,
                };
              })
              .filter((s): s is NonNullable<typeof s> => s !== null) ?? [];

          return {
            ...supplierCompany,
            nested: nestedSuppliers,
          };
        });

        set({
          itemsForAdmin: itemsWithNested,
          paginationForAdminItems: paginationData,
          error: null,
        });
      } catch (e) {
        console.error("Failed to fetch supplier companies:", e);
      } finally {
        set({ isLoading: false });
      }
    },

    fetchByOwner: async (params) => {
      set({ isLoading: true });
      try {
        const { organiserCompanyId } = params;
        const items = await apiV2.supplierCompany.getByOwner({
          organiserCompanyId,
        });
        set({ itemsByOwner: items, error: null });
      } catch (e) {
        console.error("Failed to fetch supplier companies by owner:", e);
        set({ itemsByOwner: [] });
      } finally {
        set({ isLoading: false });
      }
    },

    fetchByOrganiserCompany: async (params) => {
      set({ isLoading: true });
      try {
        const { organiserCompanyId } = params;
        const itemsByOrganiserCompany = await apiV2.supplierCompany.getSupplierCompaniesByOrganiserCompany(
          {
            organiserCompanyId,
          }
        );
        set({
          supplierCompaniesListByOrganiserCompany: itemsByOrganiserCompany,
          error: null,
        });
      } catch (e) {
        console.error(
          "Failed to fetch supplier companies by organiser company:",
          e
        );
        set({ supplierCompaniesListByOrganiserCompany: [] });
      } finally {
        set({ isLoading: false });
      }
    },

    reload: async (target) => {
      const {
        fetchManyByAdmin,
        fetchMany,
        lastUsedAdminParams,
        lastUsedSupplierParams,
      } = get();

      if (lastUsedAdminParams) {
        await fetchManyByAdmin(lastUsedAdminParams);
      }

      if (lastUsedSupplierParams) {
        await fetchMany(lastUsedSupplierParams, target);
      }
    },

    autoCatch: (target) => {
      const { reload } = get();
      const keys = [
        NotificationType.UPDATED_SUPPLIER_COMPANY,
        NotificationType.CREATED_SUPPLIER_COMPANY,
        NotificationType.DELETED_SUPPLIER_COMPANY,
        NotificationType.UPDATED_SUPPLIER,
        NotificationType.CREATED_SUPPLIER,
        NotificationType.DELETED_SUPPLIER,
        NotificationType.ARCHIVE_SUPPLIER_COMPANY,
        NotificationType.UNARCHIVE_SUPPLIER_COMPANY,
        NotificationType.ASSIGN_SUPPLIER_COMPANY,
      ];

      for (const key of keys) {
        apiV2.notification.subscribe({
          key,
          listenerId: `supplier_company_store_${target}_${key}`,
          callback: () => reload(target),
        });
      }
    },

    autoCatchForAssignedToShow: (params) => {
      const { fetchSupplierCompaniesAssignedToShow } = get();
      const keys = [
        NotificationType.UPDATED_SUPPLIER_COMPANY,
        NotificationType.CREATED_SUPPLIER_COMPANY,
        NotificationType.DELETED_SUPPLIER_COMPANY,
        NotificationType.ARCHIVE_SUPPLIER_COMPANY,
        NotificationType.UNARCHIVE_SUPPLIER_COMPANY,
        NotificationType.ASSIGN_SUPPLIER_COMPANY,
        NotificationType.ASSIGN_SUPPLIER_COMPANY_TO_SHOW,
        NotificationType.EDIT_SUPPLIER_COMPANY_TO_SHOW,
        NotificationType.DELETE_SUPPLIER_COMPANY_FROM_SHOW,
      ];

      for (const key of keys) {
        apiV2.notification.subscribe({
          key,
          listenerId: `supplier_company_assigned_to_show_store_${params.showId}_${key}`,
          callback: () => fetchSupplierCompaniesAssignedToShow(params),
        });
      }
    },

    autoCatchForAssignableToShow: (params) => {
      const { fetchSupplierCompaniesAssignableToShow } = get();
      const keys = [
        NotificationType.UPDATED_SUPPLIER_COMPANY,
        NotificationType.CREATED_SUPPLIER_COMPANY,
        NotificationType.DELETED_SUPPLIER_COMPANY,
        NotificationType.ARCHIVE_SUPPLIER_COMPANY,
        NotificationType.UNARCHIVE_SUPPLIER_COMPANY,
        NotificationType.ASSIGN_SUPPLIER_COMPANY,
        NotificationType.ASSIGN_SUPPLIER_COMPANY_TO_SHOW,
        NotificationType.EDIT_SUPPLIER_COMPANY_TO_SHOW,
        NotificationType.DELETE_SUPPLIER_COMPANY_FROM_SHOW,
      ];

      for (const key of keys) {
        apiV2.notification.subscribe({
          key,
          listenerId: `supplier_company_assignable_to_show_store_${params.showId}_${key}`,
          callback: () => fetchSupplierCompaniesAssignableToShow(params),
        });
      }
    },

    fetchOne: async (params) => {
      try {
        const data = await apiV2.supplierCompany.getOne(params);
        set({ selected: data, error: null });
      } catch (e) {
        console.error("Failed to fetch supplier company:", e);
        set({ selected: null });
      }
    },

    fetchShowManager: async (params) => {
      try {
        const data = await apiV2.supplierCompany.getSupplierCompanyShowManager(
          params
        );
        set({ showManager: data ?? null, error: null });
      } catch (e) {
        console.error("Failed to fetch supplier company show manager:", e);
        set({ showManager: null });
      }
    },

    edit: async (body) => {
      try {
        const res = await apiV2.supplierCompany.edit(body);
        set((state) => ({
          itemsForAdmin: state.itemsForAdmin.map((item) =>
            item.id === res.id ? res : item
          ),
          itemsForOrganiser: state.itemsForRecommended.map((item) =>
            item.id === res.id ? res : item
          ),
          itemsForOrganiserCompany: state.itemsForOrganiserCompany.map((item) =>
            item.id === res.id ? res : item
          ),
          error: null,
        }));
      } catch (e) {
        console.error("Failed to edit supplier company:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    editProductCategoryCode: async (body) => {
      try {
        const res = await apiV2.supplierCompany.editProductCategoryCode(body);
        set((state) => ({
          itemsSupplierCompanyProductCategory: state.itemsSupplierCompanyProductCategory.map(
            (item) => (item.id === res.id ? res : item)
          ),
          error: null,
        }));
      } catch (e) {
        console.error(
          "Failed to edit supplier company product category code:",
          e
        );
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    changeRecommendations: async (body) => {
      try {
        await apiV2.supplierCompany.changeRecommendations(body);
      } catch (e) {
        console.error("Failed to change supplier company recommendations:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    changeInvoiceEmails: async (body) => {
      try {
        const data = await apiV2.supplierCompany.changeInvoiceEmails(body);
        set({ showManager: data, error: null });
      } catch (e) {
        console.error("Failed to change supplier company invoice emails:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    changeStatus: async (body) => {
      try {
        await apiV2.supplierCompany.changeStatus(body);
      } catch (e) {
        console.error(
          "Failed to change supplier company archive/unarchive:",
          e
        );
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    assign: async (body) => {
      try {
        await apiV2.supplierCompany.assign(body);
      } catch (e) {
        console.error("Failed to change supplier company assign:", e);
        const message = getErrorMessage(e);
        set({ error: message });
        throw message;
      }
    },

    fetchAllSupplierCompaniesToProductCategory: async (params) => {
      try {
        const data = await apiV2.supplierCompany.getAllSupplierCompaniesToProductCategory(
          params
        );
        set({ itemsSupplierCompanyProductCategory: data, error: null });
      } catch (e) {
        console.error("Failed to fetch supplier company product category:", e);
        set({ selected: null });
      }
    },

    fetchAllSupplierCompaniesShowProductCategory: async (params) => {
      try {
        const data = await apiV2.supplierCompany.getAllSupplierCompaniesShowProductCategory(
          params
        );
        set({ itemsSupplierCompanyShowProductCategory: data, error: null });
      } catch (e) {
        console.error(
          "Failed to fetch supplier company show product category:",
          e
        );
        set({ selected: null });
      }
    },

    fetchOneSupplierCompaniesToProductCategory: async (params) => {
      try {
        const data = await apiV2.supplierCompany.getOneSupplierCompaniesToProductCategory(
          params
        );
        set({ selectedSupplierCompanyProductCategory: data, error: null });
      } catch (e) {
        console.error("Failed to fetch supplier company product category:", e);
        set({ selected: null });
      }
    },

    clearSelected: () => set({ selected: null }),
  }))
);

registerStore(useSupplierCompanies);
